import * as React from 'react';
import { OpportunityStage, EnquirySource, EnquiryType, EnquiryStatus } from '@cdf/api-client-react';
import { Link, useLocation, type LinkProps } from 'react-router-dom';
import { useAuth } from '@/components/auth/auth-provider';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';

export interface NativeListState {
  search: string;
  filters: Record<string, string>;
  sort: string;
  offset: number;
  pageSize: number;
  selectedId?: string;
}
interface InteractionState {
  lists: Map<string, NativeListState>;
  returns: Map<string, string>;
}
const Context = React.createContext<InteractionState | null>(null);

/** One memory-only store per current actor/scope. No records, draft authority or browser storage. */
export function InteractionProvider({ children }: { children: React.ReactNode }) {
  const [value] = React.useState<InteractionState>(() => ({ lists: new Map(), returns: new Map() }));
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function ScopedInteractionProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const access = useTenantEntity();
  const key = JSON.stringify([auth.account?.stableId ?? '', access.selected?.tenantId,
    access.selected?.entityId, access.status]);
  return <InteractionProvider key={key}>{children}</InteractionProvider>;
}
export function useNativeListState(key: string, initial: NativeListState) {
  const store = React.useContext(Context);
  const [local, setLocal] = React.useState(() => ({ key, value: store?.lists.get(key) ?? initial }));
  const value = local.key === key ? local.value : store?.lists.get(key) ?? initial;
  function update(patch: Partial<NativeListState>, resetPage = false) {
    const next = { ...(store?.lists.get(key) ?? value), ...patch, ...(resetPage ? { offset: 0, selectedId: undefined } : {}) };
    store?.lists.set(key, next);
    setLocal({ key, value: next });
  }
  return [value, update] as const;
}
function safeOrigin(pathname: string, search: string) {
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return '/';
  // Only known presentation enums/IDs. Free text and arbitrary query values never enter return links.
  const source = new URLSearchParams(search), query = new URLSearchParams();
  const allowed: Record<string, readonly string[]> = {
    view: ['all', 'b2b', 'b2c', 'partner', 'investor'],
    relationshipType: ['b2b_customer', 'b2c_consumer', 'partner', 'supplier', 'investor', 'prospect', 'other'],
    audience: ['all', 'b2b', 'b2c', 'partner', 'investor'],
    pipeline: ['all', 'b2b_sales', 'b2c_sales', 'investor_funding'],
    stage: ['all', ...Object.values(OpportunityStage)],
    status: ['all', ...Object.values(EnquiryStatus)],
    source: ['all', ...Object.values(EnquirySource)],
    type: ['all', ...Object.values(EnquiryType)]
  };
  for (const [name, values] of Object.entries(allowed)) {
    const value = source.get(name);
    if (value && values.includes(value)) query.set(name, value);
  }
  if (pathname === '/manage/proposals' || pathname === '/manage/tenders') {
    const type = source.get('type'), status = source.get('status'), page = source.get('page');
    query.delete('type'); query.delete('status');
    if (type && ['tender', 'sow'].includes(type)) query.set('type', type);
    if (status && ['draft', 'in_preparation', 'in_review', 'changes_requested', 'approved', 'submitted', 'closed', 'archived', 'published', 'needs_update'].includes(status)) query.set('status', status);
    if (page && /^\d+$/.test(page) && Number.isSafeInteger(Number(page)) && Number(page) <= Math.floor(Number.MAX_SAFE_INTEGER / 25)) query.set('page', page);
    for (const name of ['accountId', 'quoteRequestId']) {
      const id = source.get(name);
      if (id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) query.set(name, id);
    }
  }
  if (pathname === '/manage/sows') {
    const status = source.get('status'), page = source.get('page');
    query.delete('status');
    if (status && ['active', 'archived', 'all'].includes(status)) query.set('status', status);
    if (page && /^\d+$/.test(page) && Number.isSafeInteger(Number(page)) && Number(page) <= 4000) query.set('page', page);
    for (const name of ['accountId', 'opportunityId', 'quoteId']) {
      const id = source.get(name);
      if (id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) query.set(name, id);
    }
  }
  if (/^\/manage\/sows\/[0-9a-f-]{36}$/i.test(pathname)) {
    query.delete('status');
    const tab = source.get('tab');
    if (tab && ['overview', 'files', 'collateral', 'scope', 'setup', 'drafts', 'review', 'history'].includes(tab)) query.set('tab', tab);
  }
  if (/^\/manage\/tenders\/[0-9a-f-]{36}$/i.test(pathname)) {
    const tab = source.get('tab');
    if (tab && ['agent', 'files', 'collateral', 'grounding', 'requirements', 'response', 'review', 'ai', 'overview'].includes(tab)) query.set('tab', tab);
  }
  const segmentId = source.get('segmentId');
  if (pathname === '/partner-engagement/accounts' && segmentId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segmentId)) query.set('segmentId', segmentId);
  const entry = source.get('entry');
  if (pathname === '/files' && entry && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(entry)) query.set('entry', entry);
  if (pathname === '/files') {
    const grouping = source.get('grouping');
    if (grouping && ['tenders', 'campaigns', 'accounts'].includes(grouping)) query.set('grouping', grouping);
    for (const name of ['tenderId', 'campaignId', 'accountId']) {
      const id = source.get(name);
      if (id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) query.set(name, id);
    }
  }
  return pathname + (query.size ? '?' + query.toString() : '');
}
export function RecordLink({ onClick, ...props }: LinkProps) {
  const store = React.useContext(Context);
  const location = useLocation();
  return <Link {...props} onClick={event => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || props.target === '_blank') return;
    const path = typeof props.to === 'string' ? props.to.split(/[?#]/)[0] : props.to.pathname;
    if (path?.startsWith('/') && !path.startsWith('//') && path !== location.pathname) {
      store?.returns.set(path, safeOrigin(location.pathname, location.search));
    }
  }} />;
}
export function useRecordReturn(fallback: string) {
  const store = React.useContext(Context);
  const { pathname } = useLocation();
  return store?.returns.get(pathname) ?? fallback;
}

/** Correct deleted/out-of-range last pages only after an authoritative successful response. */
export function lastPageOffset(total: number, pageSize: number) {
  return Math.max(0, Math.ceil(Math.max(0, total) / Math.max(1, pageSize)) - 1) * Math.max(1, pageSize);
}
